import React, { useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { Response, ResponseError } from 'superagent';
import { Default as Alert } from '@gjensidige/core-alerts';
import { AlertTypeProps } from '@gjensidige/core-alerts/lib/types';
import utils from '@gjensidige/core-utilities-css';
import {
  ActionButton,
  CriticalButton,
  SecondaryButton
} from '@gjensidige/nci-core-buttons';
import { Input } from '@gjensidige/nci-core-forms/lib/input';
import { KeyboardArrowLeft } from '@gjensidige/nci-core-icons';
import { Text, Title } from '@gjensidige/nci-core-typography';

import styles from '../assets/styles/shared.module.scss';
import { registerUser } from '../service/api';

export const RegisterUser: React.FunctionComponent = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nameInvalid, setNameInvalid] = useState(false);
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordInvalid, setPasswordInvalid] = useState(false);
  const [confirmPasswordInvalid, setConfirmPasswordInvalid] = useState(false);
  const [message, setMessage] = useState({
    type: '' as AlertTypeProps['alertType'],
    text: ''
  });
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const handleClick = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    const response = await registerUser(name, email, password);

    if (response.status === 200) {
      setLoading(false);
      const responseSuccess = response as Response;
      setMessage({
        type: 'success',
        text:
          responseSuccess.body?.success[0] ??
          `Request for user with email: "${email}" has been sent to Team Builders for verification 🛠`
      });
    } else {
      setLoading(false);
      const responseError = response as ResponseError;
      setMessage({
        type: 'error',
        text:
          responseError.response?.body?.errors[0] ??
          'Something went wrong when trying to register user, please try again or contact Team Builders'
      });
    }
  };

  const checkInputs = () => {
    name === '' ? setNameInvalid(true) : setNameInvalid(false);
    email === '' ? setEmailInvalid(true) : setEmailInvalid(false);
    password === '' ? setPasswordInvalid(true) : setPasswordInvalid(false);
    confirmPassword === password
      ? setConfirmPasswordInvalid(false)
      : setConfirmPasswordInvalid(true);
  };

  const renderForm = () => {
    return (
      <div className={classNames(styles.overrides, utils['ml-md-4'])}>
        {message.type && (
          <Alert
            alertType={message.type as AlertTypeProps['alertType']}
            className={utils['mb-2']}
          >
            <Text size="p">{message.text}</Text>
          </Alert>
        )}
        <Title size="h3" tag="h1">
          Register new user
        </Title>
        <form
          className={utils['mt-2']}
          action="#"
          onSubmit={(event) => handleClick(event)}
        >
          <Input
            id="name"
            name="name"
            labelText="Enter full name"
            errorText="Name is required"
            invalid={nameInvalid}
            autoComplete="username"
            value={name}
            onChange={(event: {
              currentTarget: { value: React.SetStateAction<string> };
            }) => setName(event.currentTarget.value)}
            required={true}
            className={styles.inputWidth}
          />
          <Input
            id="email"
            name="email"
            type="email"
            labelText="Enter email"
            errorText="Email is required"
            autoComplete="email"
            invalid={emailInvalid}
            value={email}
            onChange={(event: {
              currentTarget: { value: React.SetStateAction<string> };
            }) => setEmail(event.currentTarget.value)}
            required={true}
            className={classNames(styles.inputWidth, utils['mt-3'])}
          />
          <Input
            type="password"
            id="password"
            name="password"
            labelText="Set a password"
            errorText="Password is required"
            autoComplete="new-password"
            invalid={passwordInvalid}
            value={password}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(event.currentTarget.value)
            }
            required={true}
            className={classNames(styles.inputWidth, utils['mt-3'])}
          />

          <Input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            labelText="Confirm password"
            errorText="The inputfields does not match"
            autoComplete="new-password"
            invalid={confirmPasswordInvalid}
            value={confirmPassword}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setConfirmPassword(event.currentTarget.value)
            }
            required={true}
            className={classNames(styles.inputWidth, utils['mt-3'])}
          />
          <CriticalButton
            className={classNames(utils['mt-4'])}
            type="submit"
            loading={loading}
            onClick={() => checkInputs()}
          >
            Register new user
          </CriticalButton>
        </form>
        <ActionButton
          className={classNames(styles.secondaryButton, utils['mt-3'])}
          onClick={(event: React.SyntheticEvent<HTMLButtonElement>) => {
            event.preventDefault();
            history.push('/login');
          }}
          icon={<KeyboardArrowLeft />}
        >
          Go back
        </ActionButton>
      </div>
    );
  };

  const renderSuccess = () => {
    return (
      <>
        {message.type && (
          <Alert
            alertType={message.type as AlertTypeProps['alertType']}
            className={utils['mb-2']}
          >
            <Text size="p">{message.text}</Text>
          </Alert>
        )}
        <div className={utils['mt-3']}>
          <SecondaryButton
            className={classNames(styles.secondaryButtonWidth, utils['mt-1'])}
            onClick={(event: React.SyntheticEvent<HTMLButtonElement>) => {
              event.preventDefault();
              history.push('/login');
            }}
          >
            Go to login page
          </SecondaryButton>
        </div>
      </>
    );
  };

  return <>{message.type === 'success' ? renderSuccess() : renderForm()}</>;
};
